import type { DateValue, ValidationResult } from "react-aria-components";
import {
  Text,
  DatePicker as RACDatePicker,
  Group,
  DateInput,
  DateSegment,
  Button,
  Popover,
  Dialog,
  Calendar,
  Heading,
  CalendarGrid,
  CalendarCell,
  FieldError,
} from "react-aria-components";
import clsx from "clsx";

import Icon from "../icon";
import LeftCaret from "../../icons/left-caret.svg?react";
import RightCaret from "../../icons/right-caret.svg?react";

import Label from "./label";
import styles from "./date-picker.module.css";

interface DatePickerProps {
  autofocus?: boolean;
  className?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  maxValue?: DateValue;
  minValue?: DateValue;
  onChange?: (value: DateValue | null) => void;
  validate?: (value: DateValue) => string | null;
  value?: DateValue;
}

function DatePicker({
  onChange,
  value,
  minValue,
  maxValue,
  isDisabled = false,
  isRequired = false,
  autofocus = false,
  className = "",
  label,
  description,
  errorMessage,
  validate,
}: DatePickerProps) {
  const isInvalid = errorMessage ? { isInvalid: true } : {};
  return (
    <RACDatePicker
      value={value ?? null}
      onChange={onChange}
      minValue={minValue}
      maxValue={maxValue}
      isDisabled={isDisabled}
      isRequired={isRequired}
      autoFocus={autofocus}
      {...isInvalid}
      validate={validate}
      data-testid="pallet-date-picker"
      className={clsx(styles.datePicker, className)}
    >
      <Label>{label}</Label>
      <Group className={styles.group}>
        <DateInput data-testid="pallet-date-input" className={styles.dateInput}>
          {(segment) => (
            <DateSegment className={styles.segment} segment={segment} />
          )}
        </DateInput>
        <Button data-testid="pallet-button-trigger" className={styles.button}>
          <Icon name="Calendar" size={18} data-testid="calendar" />
        </Button>
      </Group>
      {description && (
        <Text data-testid="pallet-description" slot="description">
          {description}
        </Text>
      )}
      <FieldError data-testid="pallet-field-error" className={styles.error}>
        {errorMessage}
      </FieldError>
      <Popover className={styles.popover}>
        <Dialog>
          <Calendar className={styles.calendar}>
            <header className={styles.calendarHeader}>
              <Button
                data-testid="pallet-prev-month-button"
                className={styles.button}
                slot="previous"
              >
                <LeftCaret data-testid="pallet-left-caret" />
              </Button>
              <Heading className={styles.month} />
              <Button
                data-testid="pallet-next-month-button"
                className={styles.button}
                slot="next"
              >
                <RightCaret data-testid="pallet-right-caret" />
              </Button>
            </header>
            <CalendarGrid className={styles.calendarGrid}>
              {(date) => (
                <CalendarCell className={styles.calendarCell} date={date} />
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </RACDatePicker>
  );
}

export default DatePicker;
